"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type SnorkelingDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A split-level lagoon: blue sky and a palm island above the waterline, a
 * snorkeller floating face-down at the surface with the tube poking out (the
 * focal point), and a sunlit coral garden with a clownfish and a yellow tang
 * below. Corals hug the flanks so the sand under the bottom-centre participant
 * pill stays open.
 *
 * Motion: the caustic net drifts and the surface sparkles — CSS transform and
 * opacity only, off under reduced motion.
 */
const SNORKEL_KEYFRAMES = `
@keyframes snorkel-caustics { 0%, 100% { transform: translate3d(0, 0, 0) } 50% { transform: translate3d(-8px, 3px, 0) } }
@keyframes snorkel-sparkle { 0%, 100% { opacity: 0.25 } 50% { opacity: 1 } }
.snorkel-caustics { animation: snorkel-caustics 7s ease-in-out infinite }
.snorkel-sparkle { animation: snorkel-sparkle 2.4s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .snorkel-caustics, .snorkel-sparkle { animation: none }
}
`;

/** Where the waterline sits, in px from the top of the 220px scene. */
const WATERLINE = 62;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `snorkel-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#4fb8e6" />
          <stop offset="1" stopColor="#bdeefa" />
        </linearGradient>
        <linearGradient id={id("water")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#3fd6d4" />
          <stop offset="0.35" stopColor="#14a9b8" />
          <stop offset="1" stopColor="#086079" />
        </linearGradient>
        <linearGradient id={id("ray")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#f2fff9" stopOpacity="0.35" />
          <stop offset="1" stopColor="#f2fff9" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("sand")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#a6e0cc" />
          <stop offset="1" stopColor="#5fa99d" />
        </linearGradient>
        <linearGradient id={id("fin")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#1f7fe0" />
          <stop offset="1" stopColor="#5fc0ff" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Sky, surface, rays and sand — stretches with the card. */
function Lagoon({ id }: { id: IdFn }) {
  const w = WATERLINE;
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="300" height={w} fill={`url(#${id("sky")})`} />
      <rect
        x="0"
        y={w}
        width="300"
        height={220 - w}
        fill={`url(#${id("water")})`}
      />
      {/* Light rays under the surface */}
      <polygon
        points={`40,${w} 66,${w} 104,190 74,190`}
        fill={`url(#${id("ray")})`}
      />
      <polygon
        points={`150,${w} 168,${w} 196,180 172,180`}
        fill={`url(#${id("ray")})`}
      />
      <polygon
        points={`226,${w} 252,${w} 284,196 254,196`}
        fill={`url(#${id("ray")})`}
      />
      {/* Surface: bright meniscus, then the underside's ripple */}
      <path
        d={`M0 ${w} Q12 ${w - 3} 24 ${w} T48 ${w} T72 ${w} T96 ${w} T120 ${w} T144 ${w} T168 ${w} T192 ${w} T216 ${w} T240 ${w} T264 ${w} T288 ${w} T312 ${w} V${w + 6} H0 Z`}
        fill="#7ff0ea"
        opacity="0.65"
      />
      <path
        d={`M0 ${w} Q12 ${w - 3} 24 ${w} T48 ${w} T72 ${w} T96 ${w} T120 ${w} T144 ${w} T168 ${w} T192 ${w} T216 ${w} T240 ${w} T264 ${w} T288 ${w} T312 ${w}`}
        stroke="#ffffff"
        strokeOpacity="0.9"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Sand */}
      <path
        d="M0 186 Q70 174 150 180 Q230 174 300 186 L300 220 L0 220 Z"
        fill={`url(#${id("sand")})`}
      />
    </svg>
  );
}

/** Caustic net — its own layer so it can drift on the compositor. */
function Caustics() {
  return (
    <svg
      className="snorkel-caustics absolute -inset-x-4 top-[66px] bottom-0 h-[calc(100%-66px)] w-[calc(100%+32px)]"
      viewBox="0 0 320 154"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M0 14 Q20 4 40 16 T80 12 T120 18 T160 10 T200 16 T240 10 T280 18 T320 12 M0 40 Q24 30 46 42 T92 36 T138 44 T184 34 T230 42 T276 36 T320 42 M10 0 Q4 20 14 40 T12 80 M70 0 Q78 22 66 42 T74 84 M140 0 Q132 20 144 44 T136 82 M210 0 Q218 24 206 44 T214 80 M280 0 Q272 22 284 42 T276 84 M0 130 Q30 120 60 132 T120 126 T180 134 T240 124 T320 132"
        stroke="#e9fff8"
        strokeOpacity="0.16"
        strokeWidth="1.4"
        fill="none"
      />
    </svg>
  );
}

function Island() {
  return (
    <svg
      className="absolute left-[5%]"
      style={{ top: WATERLINE - 34 }}
      width="78"
      height="36"
      viewBox="0 0 78 36"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0 35 Q20 24 40 27 Q58 25 78 35 Z" fill="#2c7f63" />
      <path
        d="M8 35 Q26 28 46 31 Q62 30 72 35 Z"
        fill="#e8d59a"
        opacity="0.8"
      />
      {/* Palms */}
      <path
        d="M30 28 Q32 16 26 6"
        stroke="#6b4a2b"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M44 28 Q44 18 50 10"
        stroke="#6b4a2b"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M26 6 Q18 2 12 8 M26 6 Q20 0 26 -1 M26 6 Q32 0 38 4 M26 6 Q34 6 36 12 M26 6 Q18 8 16 14"
        stroke="#1f6b4a"
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M50 10 Q44 4 38 9 M50 10 Q50 3 56 3 M50 10 Q58 6 62 12 M50 10 Q56 14 56 18"
        stroke="#2a8a5e"
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function Cloud({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="46"
      height="16"
      viewBox="0 0 46 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4 15 Q0 9 7 8 Q8 2 15 4 Q20 -1 27 4 Q34 1 37 8 Q46 8 42 15 Z"
        fill="#ffffff"
        opacity="0.85"
      />
    </svg>
  );
}

function Snorkeller({ id }: { id: IdFn }) {
  const suit = "#0f3446";
  // Waterline sits at y=16 inside this svg.
  return (
    <svg
      className="absolute left-[calc(50%-24px)]"
      style={{ top: WATERLINE - 16 }}
      width="132"
      height="48"
      viewBox="0 0 132 48"
      aria-hidden="true"
      focusable="false"
    >
      {/* Snorkel tube, breaking the surface */}
      <path
        d="M22 26 Q25 18 25 2"
        stroke="#1c2226"
        strokeWidth="3.2"
        strokeLinecap="round"
        fill="none"
      />
      <rect x="22.6" y="-1" width="4.8" height="7" rx="1.6" fill="#ff7a2e" />
      <rect x="23.4" y="0" width="1.4" height="5" rx="0.7" fill="#ffd2b0" />
      {/* Ripple ring where the tube meets the water */}
      <ellipse
        cx="25"
        cy="16"
        rx="9"
        ry="1.6"
        fill="none"
        stroke="#fff"
        strokeOpacity="0.8"
        strokeWidth="0.9"
      />
      <g opacity="0.92">
        {/* Fins */}
        <path
          d="M104 22 L128 17 Q131 23 127 27 L104 28 Z"
          fill={`url(#${id("fin")})`}
        />
        <path d="M102 28 L124 32 Q124 38 119 38 L101 33 Z" fill="#1667b8" />
        {/* Legs */}
        <path
          d="M78 23 L92 22 L105 25"
          stroke={suit}
          strokeWidth="6.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M78 27 L91 30 L103 30"
          stroke="#0a2533"
          strokeWidth="6.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Torso — a coral rash guard */}
        <path
          d="M28 20 Q34 15 50 16 L80 20 Q85 25 80 30 L50 31 Q34 31 28 26 Z"
          fill="#ff6f59"
        />
        <path d="M66 18.5 L80 20 Q85 25 80 30 L66 30.5 Z" fill={suit} />
        {/* Arms along the body */}
        <path
          d="M38 27 L52 32 L66 31"
          stroke="#e0583f"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Head, face down, mask */}
        <circle cx="22" cy="24" r="7" fill="#3b2418" />
        <rect x="14" y="25" width="10" height="6.5" rx="2" fill="#1c2226" />
        <rect
          x="15.4"
          y="26.2"
          width="7.2"
          height="4"
          rx="1.4"
          fill="#9ef2ff"
        />
        <rect x="16.2" y="26.8" width="1.8" height="2" rx="0.8" fill="#fff" />
        {/* Sun on the back, through the surface */}
        <path
          d="M29 19.5 Q35 15 50 16 L80 20 M78 23 L92 22 L105 25"
          stroke="#ffffff"
          strokeOpacity="0.55"
          strokeWidth="1"
          fill="none"
        />
      </g>
    </svg>
  );
}

function Fish() {
  return (
    <>
      {/* Yellow tang */}
      <svg
        className="absolute top-[104px] left-[16%]"
        width="22"
        height="18"
        viewBox="0 0 22 18"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M2 9 Q6 1 14 3 Q17 6 17 9 Q17 12 14 15 Q6 17 2 9 Z"
          fill="#ffd21f"
        />
        <path d="M16 9 L22 4 L21 9 L22 14 Z" fill="#ffc400" />
        <circle cx="6" cy="8" r="1" fill="#1a1a1a" />
        <path
          d="M5 4 Q10 2 14 3"
          stroke="#fff6b0"
          strokeWidth="0.8"
          fill="none"
        />
      </svg>
      {/* Blue tang */}
      <svg
        className="absolute top-[132px] right-[30%]"
        width="20"
        height="13"
        viewBox="0 0 20 13"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M18 6.5 Q14 0 6 2 Q3 4 3 6.5 Q3 9 6 11 Q14 13 18 6.5 Z"
          fill="#2b6fe0"
        />
        <path d="M4 6.5 L0 2.5 L1 6.5 L0 10.5 Z" fill="#f5c400" />
        <path
          d="M8 4 Q12 6 15 5"
          stroke="#0b1f4a"
          strokeWidth="1.2"
          fill="none"
        />
        <circle cx="15" cy="5.6" r="0.9" fill="#0b1f4a" />
      </svg>
    </>
  );
}

function LeftCorals() {
  return (
    <svg
      className="absolute bottom-0 left-0"
      width="104"
      height="78"
      viewBox="0 0 104 78"
      aria-hidden="true"
      focusable="false"
    >
      {/* Purple sea fan */}
      <path
        d="M22 54 C 2 42, 2 14, 18 8 C 32 4, 42 20, 22 54 Z"
        fill="#9b6ad6"
      />
      <path
        d="M22 54 L10 20 M22 54 L20 8 M22 54 L32 16 M12 34 L30 30"
        stroke="#5b3593"
        strokeWidth="1"
        fill="none"
      />
      {/* Pink branching coral */}
      <path
        d="M52 70 L48 46 L40 34 M48 46 L56 32 M52 70 L62 52 L70 42 M62 52 L60 38"
        stroke="#ff7fa6"
        strokeWidth="4.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Orange brain coral */}
      <ellipse cx="80" cy="66" rx="16" ry="10" fill="#ff9e45" />
      <path
        d="M67 66 q4 -4 8 0 t8 0 t8 0 M70 61 q4 -3 8 0 t8 0"
        stroke="#c4621a"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M66 62 Q78 54 94 62"
        stroke="#ffd7a8"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Sand mound */}
      <path
        d="M0 78 L0 52 Q16 48 30 58 Q50 56 66 66 Q86 62 104 78 Z"
        fill="#6fb8a9"
      />
      <path
        d="M0 52 Q16 48 30 58 Q50 56 66 66 Q86 62 104 78"
        stroke="#d8fff2"
        strokeOpacity="0.5"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

function RightCorals() {
  return (
    <svg
      className="absolute right-0 bottom-0"
      width="104"
      height="84"
      viewBox="0 0 104 84"
      aria-hidden="true"
      focusable="false"
    >
      {/* Yellow staghorn */}
      <path
        d="M86 72 L88 44 L80 30 M88 44 L98 32 M86 72 L76 52"
        stroke="#ffd34d"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Anemone */}
      {[-56, -38, -20, -2, 16, 34, 52].map((deg) => (
        <path
          key={deg}
          d="M44 66 Q41 54 44 42"
          transform={`rotate(${deg} 44 66)`}
          stroke="#b7f06a"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      ))}
      {/* Clownfish */}
      <g transform="translate(26 30)">
        <path
          d="M2 7 Q8 0 18 3 Q21 5 21 7 Q21 9 18 11 Q8 14 2 7 Z"
          fill="#ff7a1a"
        />
        <path d="M20 7 L26 2.5 L25 7 L26 11.5 Z" fill="#ff7a1a" />
        <path
          d="M7 2.6 Q9 7 7 11.6 M13 2.4 Q15 7 13 11.8"
          stroke="#fff"
          strokeWidth="2"
          fill="none"
        />
        <circle cx="4.6" cy="6" r="0.9" fill="#1a1a1a" />
      </g>
      {/* Sand mound */}
      <path
        d="M0 84 Q20 66 40 68 Q62 62 80 70 Q94 66 104 70 L104 84 Z"
        fill="#6fb8a9"
      />
      <path
        d="M0 84 Q20 66 40 68 Q62 62 80 70 Q94 66 104 70"
        stroke="#d8fff2"
        strokeOpacity="0.5"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

export function SnorkelingDisplayPreview({
  participants,
}: SnorkelingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Snorkeling"
      backgroundClassName="bg-[#0a8ea3]"
      participants={participants}
      scene={
        <>
          <style href="snorkel-scene-keyframes" precedence="default">
            {SNORKEL_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          <Lagoon id={id} />
          <Caustics />
          {/* Sun, top-right */}
          <div className="absolute -top-10 right-[6%] h-24 w-24 rounded-full bg-[radial-gradient(closest-side,rgba(255,252,220,0.95),rgba(255,245,190,0.35)_45%,transparent)]" />
          <Cloud className="absolute top-[16px] left-[30%] opacity-80" />
          <Cloud className="absolute top-[34px] right-[24%] scale-75 opacity-70" />
          <Island />
          {/* Surface glints */}
          <div
            className="snorkel-sparkle absolute left-[36%] h-[2px] w-3 rounded-full bg-white"
            style={{ top: WATERLINE - 1 }}
          />
          <div
            className="snorkel-sparkle absolute right-[14%] h-[2px] w-4 rounded-full bg-white [animation-delay:1.2s]"
            style={{ top: WATERLINE - 1 }}
          />

          <Snorkeller id={id} />
          <Fish />
          <LeftCorals />
          <RightCorals />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_120%_120%_at_50%_35%,transparent_55%,rgba(2,40,52,0.45)_100%)]" />
      }
    />
  );
}
